import {
  DeleteOutlined,
  ExperimentOutlined,
  MessageOutlined,
  NotificationOutlined,
  SaveOutlined,
  SlidersOutlined,
  UploadOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  Alert,
  App,
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography,
  Upload,
} from "antd";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/entities/session";
import type { ProjectSettings } from "@/entities/settings";
import { useSettings } from "@/entities/settings";
import { useEditSettings, useUploadBroadcastImage } from "@/features/edit-settings";
import { errorMessage } from "@/shared/api";
import { SECTIONS } from "@/shared/config";
import { PageHeader, SectionCard } from "@/shared/ui";

/** Окна подиума. Те же, что у экрана рейтинга: «месяц» обязан означать одно и
 *  то же во всём продукте. */
const LEADERBOARD_PERIODS = ["day", "week", "month", "all"] as const;

type Values = ProjectSettings;

/**
 * Настройки проекта.
 *
 * Оператор их видит, но не правит: смена времени начисления или минимальных
 * сумм касается всех пользователей сразу. Бэкенд это и так проверяет — форма
 * лишь блокирует поля, чтобы не давать заполнить то, что не сохранится.
 *
 * **Редактора адресов пополнения здесь больше нет.** Он был списком «актив →
 * сеть → адрес», который оператор заполнял руками, и адрес был ОДИН на всех
 * пользователей. С переходом на NOWPayments адрес выдаёт шлюз отдельно на
 * каждый платёж — править здесь стало нечего, а хранить в редактируемой
 * таблице ключи от горячего кошелька незачем.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isSuperadmin } = useAuth();
  const { data, isPending, isError, error } = useSettings();
  const mutation = useEditSettings();
  const upload = useUploadBroadcastImage();
  const [form] = Form.useForm<Values>();

  if (isError) return <Alert type="error" showIcon title={errorMessage(error)} />;
  if (isPending) return <Skeleton active />;

  const initial: Values = data;

  return (
    <>
      <PageHeader
        section="settings"
        title={t("settings.title")}
        subtitle={t("settings.subtitle")}
      />

      {!isSuperadmin && (
        <Alert type="info" showIcon title={t("settings.readOnly")} style={{ marginBottom: 16 }} />
      )}

      <Form<Values>
        form={form}
        layout="vertical"
        initialValues={initial}
        disabled={!isSuperadmin}
        onFinish={(values) => {
          // `broadcast_image_key` и `broadcast_image_url` в форме только
          // показываются: ключ появляется загрузкой файла, а URL вообще
          // считается на отдаче. Отправить их в PATCH нельзя — схема
          // принимает там лишь `null`, то есть «снять баннер», и обычное
          // сохранение формы стирало бы картинку.
          const { broadcast_image_key, broadcast_image_url, ...rest } = values;
          void broadcast_image_key;
          void broadcast_image_url;
          mutation.mutate(
            rest,
            {
              onSuccess: () => {
                void message.success(t("settings.saved"));
              },
            },
          );
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <SectionCard
              color={SECTIONS.settings.color}
              icon={<SlidersOutlined aria-hidden />}
              title={t("settings.limits")}
            >
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="min_deposit" label={t("settings.minDeposit")}>
                    <InputNumber stringMode min="0" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="min_withdrawal" label={t("settings.minWithdrawal")}>
                    <InputNumber stringMode min="0" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="referral_bonus" label={t("settings.referralBonus")}>
                    <InputNumber stringMode min="0" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="support_link"
                    label={t("settings.supportLink")}
                    extra={t("settings.supportLinkHint")}
                  >
                    <Input placeholder="@username" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="accrual_time"
                    label={t("settings.accrualTime")}
                    extra={t("settings.accrualTimeHint")}
                    rules={[{ pattern: /^\d{2}:\d{2}$/, message: "ЧЧ:ММ" }]}
                  >
                    <Input placeholder="00:00" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="accrual_tz" label={t("settings.accrualTz")}>
                    <Input placeholder="Europe/Moscow" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="loss_alert_pct" label={t("settings.lossAlertPct")}>
                    <InputNumber stringMode min="0" max="100" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="profit_milestone_step_pct"
                    label={t("settings.profitMilestonePct")}
                  >
                    <InputNumber stringMode min="0.01" max="100" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="intraday_amplitude"
                    label={t("settings.intradayAmplitude")}
                    extra={t("settings.intradayAmplitudeHint")}
                  >
                    <InputNumber stringMode min="0" max="5" step="0.05" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {/* Порог показа «Лучших результатов» и «Распределения» на
                      экране робота (ТЗ §15, §16, §33). Это единственные
                      немоделированные цифры витрины, и решать, с какого числа
                      покупателей они перестают быть случайностью, — работа
                      оператора, а не запрос в базу. */}
                  <Form.Item
                    name="robot_results_min_participants"
                    label={t("settings.robotResultsMin")}
                    extra={t("settings.robotResultsMinHint")}
                  >
                    <InputNumber min={0} max={1000} step={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {/* Окно расчёта ТОП-3 на карточке робота (доработки §5, §7
                      «Leaderboard: период расчёта»). Переключателя у
                      пользователя нет намеренно: три строки подиума — не тот
                      объём, ради которого экран обзаводится ещё одним органом
                      управления. Смена периода видна пользователю подписью
                      под заголовком блока. */}
                  <Form.Item
                    name="leaderboard_period"
                    label={t("settings.leaderboardPeriod")}
                    extra={t("settings.leaderboardPeriodHint")}
                  >
                    <Select
                      options={LEADERBOARD_PERIODS.map((value) => ({
                        value,
                        label: t(`settings.leaderboardPeriodOption.${value}`),
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="leaderboard_top_n"
                    label={t("settings.leaderboardTopN")}
                    extra={t("settings.leaderboardTopNHint")}
                  >
                    <InputNumber min={1} max={10} step={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
            </SectionCard>
          </Col>

          {/* Платёжный шлюз. Карточка только читает: ни одного поля шлюза
              оператор не задаёт — ключ и секрет живут в переменных окружения,
              а список валют в коде. Существует она ради одной строки —
              адреса колбэка: его надо вставить в кабинет NOWPayments, и без
              этой карточки оператор шёл бы за ним к разработчику. */}
          <Col xs={24} lg={12}>
            <SectionCard
              color={SECTIONS.deposits.color}
              icon={<WalletOutlined aria-hidden />}
              title={t("settings.gateway.title")}
            >
              <Typography.Paragraph type="secondary">
                {t("settings.gateway.hint")}
              </Typography.Paragraph>

              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t("settings.gateway.state")}>
                  <Tag color={data.nowpayments_enabled ? "green" : "default"}>
                    {t(data.nowpayments_enabled ? "settings.gateway.on" : "settings.gateway.off")}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t("settings.gateway.callback")}>
                  <Typography.Text copyable={{ text: data.nowpayments_callback_url }} code>
                    {data.nowpayments_callback_url || t("settings.gateway.callbackMissing")}
                  </Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label={t("settings.gateway.currencies")}>
                  {data.nowpayments_currencies?.length
                    ? data.nowpayments_currencies.map((code) => <Tag key={code}>{code}</Tag>)
                    : t("settings.gateway.noCurrencies")}
                </Descriptions.Item>
              </Descriptions>
            </SectionCard>
          </Col>

          {/* Демо-счёт (доработки §7, «Demo settings»). Отдельной карточкой, а
              не среди лимитов: это настройки другого контура, и оператор,
              крутящий стартовый баланс песочницы, не должен делать это в
              сантиметре от минимального пополнения реальными деньгами.

              «Параметров симуляции» здесь намеренно нет: демо считает по тем
              же `daily_min/daily_max/positive_chance` робота и той же
              внутридневной амплитуде, что и реальный счёт. Демо со своими
              числами не отвечает на вопрос «что будет на реальном счёте»,
              ради которого оно и существует. */}
          <Col xs={24} lg={12}>
            <SectionCard
              color={SECTIONS.robots.color}
              icon={<ExperimentOutlined aria-hidden />}
              title={t("settings.demo.title")}
            >
              <Typography.Paragraph type="secondary">
                {t("settings.demo.hint")}
              </Typography.Paragraph>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    name="demo_enabled"
                    label={t("settings.demo.enabled")}
                    extra={t("settings.demo.enabledHint")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="demo_start_balance"
                    label={t("settings.demo.startBalance")}
                  >
                    <InputNumber stringMode min="0.01" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="demo_max_positions"
                    label={t("settings.demo.maxPositions")}
                    extra={t("settings.demo.maxPositionsHint")}
                  >
                    <InputNumber min={1} max={20} step={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="demo_reset_cooldown_hours"
                    label={t("settings.demo.resetCooldown")}
                    extra={t("settings.demo.resetCooldownHint")}
                  >
                    <InputNumber min={0} max={168} step={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="demo_position_ttl_days"
                    label={t("settings.demo.ttl")}
                    extra={t("settings.demo.ttlHint")}
                  >
                    <InputNumber min={1} max={365} step={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
            </SectionCard>
          </Col>

          {/* Отзывы (доработки §6, §7 «Comments»). Премодерации среди настроек
              нет намеренно: отзывы публикуются сразу, а барьером служит право
              писать — оставить отзыв может только тот, кто запускал робота.
              Придерживается только текст со ссылкой, и это карантин спама, а
              не режим модерации. */}
          <Col xs={24} lg={12}>
            <SectionCard
              color={SECTIONS.comments.color}
              icon={<MessageOutlined aria-hidden />}
              title={t("settings.comments.title")}
            >
              <Typography.Paragraph type="secondary">
                {t("settings.comments.hint")}
              </Typography.Paragraph>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    name="comments_enabled"
                    label={t("settings.comments.enabled")}
                    extra={t("settings.comments.enabledHint")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="comment_link_quarantine"
                    label={t("settings.comments.linkQuarantine")}
                    extra={t("settings.comments.linkQuarantineHint")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="comment_rate_limit_per_hour"
                    label={t("settings.comments.perHour")}
                    extra={t("settings.comments.perHourHint")}
                  >
                    <InputNumber min={1} max={100} step={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="comment_min_interval_seconds"
                    label={t("settings.comments.interval")}
                    extra={t("settings.comments.intervalHint")}
                  >
                    <InputNumber min={0} max={3600} step={5} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="comment_edit_window_minutes"
                    label={t("settings.comments.editWindow")}
                    extra={t("settings.comments.editWindowHint")}
                  >
                    <InputNumber min={0} max={1440} step={5} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="comment_report_threshold"
                    label={t("settings.comments.reportThreshold")}
                    extra={t("settings.comments.reportThresholdHint")}
                  >
                    <InputNumber min={1} max={50} step={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
            </SectionCard>
          </Col>

          {/* Ежедневная рассылка. Живёт в настройках, а не отдельной страницей:
              у неё нет ни адресата, ни очереди, ни истории, которую оператор
              разбирает, — только семь значений и рубильник.

              Сумма разыгрывается ОДНА на всех получателей: люди сравнивают
              скриншоты, и два разных числа за один день читаются как подделка
              вернее, чем любое одно число. */}
          <Col xs={24} lg={12}>
            <SectionCard
              color={SECTIONS.accruals.color}
              icon={<NotificationOutlined aria-hidden />}
              title={t("settings.broadcast.title")}
            >
              <Typography.Paragraph type="secondary">
                {t("settings.broadcast.hint")}
              </Typography.Paragraph>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    name="broadcast_enabled"
                    label={t("settings.broadcast.enabled")}
                    extra={t("settings.broadcast.enabledHint")}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="broadcast_time"
                    label={t("settings.broadcast.time")}
                    extra={t("settings.broadcast.timeHint")}
                    rules={[{ pattern: /^\d{2}:\d{2}$/, message: "ЧЧ:ММ" }]}
                  >
                    <Input placeholder="12:00" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="broadcast_amount_min"
                    label={t("settings.broadcast.amountMin")}
                    dependencies={["broadcast_amount_max"]}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator: (_rule, value: string) =>
                          value == null ||
                          getFieldValue("broadcast_amount_max") == null ||
                          Number(value) <= Number(getFieldValue("broadcast_amount_max"))
                            ? Promise.resolve()
                            : Promise.reject(new Error(t("settings.broadcast.rangeError"))),
                      }),
                    ]}
                  >
                    <InputNumber stringMode min="0" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="broadcast_amount_max"
                    label={t("settings.broadcast.amountMax")}
                    extra={t("settings.broadcast.amountHint")}
                  >
                    <InputNumber stringMode min="0" style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="broadcast_text"
                    label={t("settings.broadcast.text")}
                    extra={t("settings.broadcast.textHint")}
                    rules={[{ max: 1024, message: t("settings.broadcast.textTooLong") }]}
                  >
                    <Input.TextArea rows={4} showCount maxLength={1024} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="broadcast_button_text"
                    label={t("settings.broadcast.buttonText")}
                    extra={t("settings.broadcast.buttonTextHint")}
                  >
                    <Input maxLength={64} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={t("settings.broadcast.banner")}
                    extra={t("settings.broadcast.bannerHint")}
                  >
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      {data.broadcast_image_url && (
                        <img
                          src={data.broadcast_image_url}
                          alt={t("settings.broadcast.banner")}
                          style={{ maxWidth: "100%", borderRadius: 8 }}
                        />
                      )}
                      <Space>
                        <Upload
                          accept="image/png,image/jpeg"
                          showUploadList={false}
                          disabled={!isSuperadmin || upload.isPending}
                          beforeUpload={(file) => {
                            upload.mutate(file);
                            // false, а не true: загрузку выполняет мутация, и
                            // встроенный аплоадер Ant не должен слать файл сам.
                            return false;
                          }}
                        >
                          <Button
                            icon={<UploadOutlined aria-hidden />}
                            loading={upload.isPending}
                            disabled={!isSuperadmin}
                          >
                            {t("settings.broadcast.bannerUpload")}
                          </Button>
                        </Upload>
                        {data.broadcast_image_key && (
                          <Button
                            danger
                            icon={<DeleteOutlined aria-hidden />}
                            disabled={!isSuperadmin || mutation.isPending}
                            onClick={() => {
                              // Снятие идёт обычным PATCH с null — тем же
                              // приёмом, что и картинка робота.
                              mutation.mutate({ broadcast_image_key: null });
                            }}
                          >
                            {t("settings.broadcast.bannerClear")}
                          </Button>
                        )}
                      </Space>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </SectionCard>
          </Col>

          <Col span={24}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined aria-hidden />}
              loading={mutation.isPending}
            >
              {t("common.save")}
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}
